import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { Table, Button, Alert, Popconfirm, Space, message } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import EmailGroupModal from './modals/EmailGroupModal';
import { deleteEmailGroup } from './api';

const EmailGroupsPanel = forwardRef(({ companyId, emailGroups, companyUsers }, ref) => {
    const { data, loading, error, page, setPage, refetch } = emailGroups;
    const [modalOpen, setModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);

    useImperativeHandle(ref, () => ({
        openAddModal: () => { setEditingGroup(null); setModalOpen(true); },
    }));

    const handleDelete = async (groupId) => {
        try {
            await deleteEmailGroup(companyId, groupId);
            message.success('Deleted successfully');
            refetch();
        } catch (err) {
            message.error(err.response?.data?.message || 'Delete failed');
        }
    };

    const columns = [
        {
            title: 'Group Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Email Subject',
            dataIndex: 'subject',
            key: 'subject',
            ellipsis: true,
        },
        {
            title: '#',
            dataIndex: 'user_count',
            key: 'user_count',
            width: 50,
        },
        {
            title: '',
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => { setEditingGroup(record); setModalOpen(true); }}
                    />
                    <Popconfirm
                        title="Are you sure you want to delete this email group?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

            <Table
                className="gh-dash-interview-table"
                columns={columns}
                dataSource={data.rows}
                loading={loading}
                rowKey="id"
                size="small"
                pagination={{
                    current: data.page,
                    pageSize: data.pageSize,
                    total: data.total,
                    onChange: (p) => setPage(p),
                    showSizeChanger: false,
                    size: 'small',
                }}
                locale={{
                    emptyText: !loading && data.total === 0 ? (
                        <div style={{ padding: 12 }}>
                            <p>No email groups found.</p>
                            <Button type="primary" onClick={() => { setEditingGroup(null); setModalOpen(true); }}>
                                + Add your first email group
                            </Button>
                        </div>
                    ) : 'No email groups found.',
                }}
            />

            <EmailGroupModal
                open={modalOpen}
                group={editingGroup}
                companyId={companyId}
                companyUsers={companyUsers}
                onClose={() => { setModalOpen(false); setEditingGroup(null); }}
                onSuccess={() => { setModalOpen(false); setEditingGroup(null); refetch(); }}
            />
        </div>
    );
});

EmailGroupsPanel.displayName = 'EmailGroupsPanel';

export default EmailGroupsPanel;
